import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { cores } from '../../theme';
import {
    Typography, Container, Box, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Alert, IconButton,
    Avatar, Fade, Dialog, DialogTitle, DialogContent, DialogActions,
    DialogContentText, Menu, MenuItem, ListItemIcon, ListItemText, Chip, Divider, useTheme
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack,
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Warning as WarningIcon,
    School as SchoolIcon,
    ManageAccounts as ManageAccountsIcon,
    BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';

const TurmasGerenciamentoPagina = () => {
    const [turmas, setTurmas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, turmaId: null, nomeTurma: '' });
    const [menuAnchorEl, setMenuAnchorEl] = useState({
        id: null,
        element: null
    });
    const navegar = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const buscarTurmas = async () => {
            try {
                const resposta = await api.get('/api/turmas');
                setTurmas(resposta.data);
            } catch (err) {
                setErro('Falha ao carregar as turmas.');
            } finally {
                setCarregando(false);
            }
        };
        buscarTurmas();
    }, []);

    const abrirMenuAcoes = (event, turmaId) => {
        setMenuAnchorEl({
            id: turmaId,
            element: event.currentTarget
        });
    };

    const fecharMenuAcoes = () => {
        setMenuAnchorEl({
            id: null,
            element: null
        });
    };

    const abrirConfirmacaoExclusao = (turmaId, nomeTurma) => {
        setConfirmarExclusao({
            aberto: true,
            turmaId,
            nomeTurma
        });
        fecharMenuAcoes();
    };

    const fecharConfirmacaoExclusao = () => {
        setConfirmarExclusao({
            aberto: false,
            turmaId: null,
            nomeTurma: ''
        });
    };

    const lidarComExcluirTurma = async () => {
        try {
            await api.delete(`/api/turmas/${confirmarExclusao.turmaId}`);
            setTurmas(turmasAtuais => turmasAtuais.filter(turma => turma.id !== confirmarExclusao.turmaId));
            fecharConfirmacaoExclusao();
        } catch (err) {
            setErro(err.response?.data?.erro || "Ocorreu um erro ao excluir a turma.");
            fecharConfirmacaoExclusao();
        }
    };

    const irParaGerenciarTurma = (turmaId) => {
        navegar(`/turmas/${turmaId}`);
        fecharMenuAcoes();
    };

    const irParaEditarTurma = (turmaId) => {
        navegar(`/turmas/editar/${turmaId}`);
        fecharMenuAcoes();
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Gerenciamento de Turmas
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Crie e gerencie turmas de alunos para os módulos
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Box>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    color: cores.primaria.principal,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                            >
                                Lista de Turmas
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {turmas.length} {turmas.length === 1 ? 'turma encontrada' : 'turmas encontradas'}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            component={RouterLink}
                            to="/turmas/criar"
                            startIcon={<AddIcon />}
                            sx={{
                                bgcolor: cores.primaria.principal,
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    bgcolor: cores.primaria.escuro,
                                }
                            }}
                        >
                            Criar Nova Turma
                        </Button>
                    </Box>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={500}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                overflow: 'hidden',
                                border: `1px solid ${cores.cinza.claro}`,
                            }}
                        >
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal, width: '40%' }}>
                                            Nome da Turma
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal, width: '20%' }}>
                                            Semestre/Ano
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal, width: '30%' }}>
                                            Módulo Associado
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {turmas.map((turma, index) => (
                                        <Fade in={true} timeout={300 + index * 100} key={turma.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc',
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Avatar
                                                            sx={{
                                                                mr: 2,
                                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                                color: cores.primaria.principal,
                                                                width: 36,
                                                                height: 36
                                                            }}
                                                        >
                                                            <SchoolIcon fontSize="small" />
                                                        </Avatar>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: '1rem',
                                                                color: cores.cinza.escuro
                                                            }}
                                                        >
                                                            {turma.nome}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={turma.semestre}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                            color: cores.secundaria.principal,
                                                            border: `1px solid ${cores.secundaria.claro}`
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {turma.modulo_nome ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <BookmarkBorderIcon
                                                                fontSize="small"
                                                                sx={{ mr: 1, color: cores.cinza.medio }}
                                                            />
                                                            <Typography sx={{ color: cores.cinza.escuro }}>
                                                                {turma.modulo_nome}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography sx={{ color: cores.cinza.medio, fontStyle: 'italic' }}>
                                                            Nenhum módulo associado
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        onClick={(e) => abrirMenuAcoes(e, turma.id)}
                                                        size="small"
                                                        sx={{
                                                            border: `1px solid ${cores.cinza.claro}`,
                                                            '&:hover': {
                                                                bgcolor: '#f1f5f9',
                                                            }
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                    {turmas.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                    Nenhuma turma encontrada.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fade>
                </Paper>
            </Fade>

            {/* Diálogo de confirmação de exclusão */}
            <Dialog
                open={confirmarExclusao.aberto}
                onClose={fecharConfirmacaoExclusao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <WarningIcon color="error" fontSize="small" />
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <DialogContentText sx={{ color: cores.cinza.escuro, mb: 2 }}>
                        Você está prestes a excluir a turma:
                    </DialogContentText>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: `1px solid ${cores.cinza.claro}`,
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: cores.cinza.escuro,
                                fontStyle: 'italic'
                            }}
                        >
                            "{confirmarExclusao.nomeTurma}"
                        </Typography>
                    </Paper>
                    <DialogContentText sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WarningIcon fontSize="small" sx={{ color: cores.erro.medio }} />
                        Esta ação não poderá ser desfeita. Todos os vínculos com alunos e professores também serão removidos.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharConfirmacaoExclusao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComExcluirTurma}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Excluir Turma
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menu de ações */}
            <Menu
                anchorEl={menuAnchorEl.element}
                open={Boolean(menuAnchorEl.element)}
                onClose={fecharMenuAcoes}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        boxShadow: cores.sombras.medio,
                        '& .MuiList-root': {
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem
                    onClick={() => irParaGerenciarTurma(menuAnchorEl.id)}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <ManageAccountsIcon fontSize="small" sx={{ color: cores.secundaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Gerenciar Turma</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={() => irParaEditarTurma(menuAnchorEl.id)}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: cores.primaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                    onClick={() => {
                        const turma = turmas.find(t => t.id === menuAnchorEl.id);
                        abrirConfirmacaoExclusao(menuAnchorEl.id, turma?.nome || '');
                    }}
                    sx={{
                        py: 1,
                        color: cores.erro.principal
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: cores.erro.principal }} />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default TurmasGerenciamentoPagina;